import {
  AysClinicIdentifier,
  AysClinicSn,
} from '@bindoc-projects/clinic-domain-lib';
import type { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';

export type RangeMode = 'drivingTime' | 'distance';

export type CenterPointSourceType = 'clinic' | 'virtualClinic' | 'address';

export interface GeoPartsAroundClinicIdValue {
  coordinate: [number, number];
  value: number;
  rangeFrom?: number;
  clinicId: AysClinicIdentifier;
  centerPointSourceType?: CenterPointSourceType;
  virtualClinicId?: string;
  administrativeDistricts: BdAdministrativeDistrictId[];
  postalCodes: string[];
}

/**
 * The centre-clinic identity a serialized geo-parts condition can carry.
 *
 * `clinicSn` is the market-api contract: BDA-5580 retyped both
 * `DistanceCondition.clinicId` and `DrivingTimeCondition.clinicId` to
 * `clinicSn: ClinicSn` — an opaque Standortnummer string, and REQUIRED on the
 * driving-time leaf, so a body still carrying the nested `clinicId` is rejected
 * outright (HTTP 400, BDA-5851).
 *
 * `clinicId` stays readable so a condition serialized before that flip — or by
 * a backend still on the old shape — keeps hydrating.
 */
export interface GeoPartsConditionClinicIdentity {
  clinicSn?: string | null;
  clinicId?: { ik: string | number; sn: string | number } | null;
}

/**
 * Write boundary: the opaque `clinicSn` for a condition's centre clinic, or
 * `null` when the centre point is a manual address or a virtual clinic.
 *
 * {@link AysClinicSn.fromIdentifier} mirrors the backend's `ClinicSn.of` — a
 * 9-digit Standortnummer passes through, a short legacy sub-number becomes the
 * synthetic `L-{ik}-{sn}` form — which is exactly how the one-shot migration
 * rewrote the stored market conditions.
 */
export function geoPartsClinicSnToCondition(
  clinicId: AysClinicIdentifier | null,
): string | null {
  return clinicId ? AysClinicSn.fromIdentifier(clinicId.ik, clinicId.sn) : null;
}

/**
 * Read boundary: accept both identity shapes (see
 * {@link GeoPartsConditionClinicIdentity}).
 *
 * An iNEK `clinicSn` carries no institution key, so the recovered identifier has
 * an empty `ik`. That is enough for every SN-native lookup — which is why both
 * the edit dialog (`AysClinicSnIdentifier.fromLegacy`) and the plugins'
 * clinic-name resolution key on the clinicSn rather than on
 * `AysClinicIdentifier#toString()`, which is null without an `ik` (BDA-5855).
 */
export function geoPartsClinicIdFromCondition(
  json: GeoPartsConditionClinicIdentity,
): AysClinicIdentifier | null {
  if (json.clinicSn) {
    return AysClinicSn.toIdentifier(json.clinicSn);
  }

  return json.clinicId ? AysClinicIdentifier.fromObj(json.clinicId) : null;
}

export interface RangeModeConfig {
  unit: string;
  defaultValue: number;
  max: number;
  /**
   * Number of labelled major ticks the slider should show along its
   * scale. Chosen so the resulting step lands on a round value in the
   * mode's unit (e.g. 6 × 10 min vs 5 × 20 km).
   */
  sliderMajorTickCount: number;
  sliderPlaceholder: string;
  sliderSuffix: string;
  validationLabel: string;
  textFn: (value: number, rangeFrom?: number) => string;
}

export const RANGE_MODE_CONFIG: Record<RangeMode, RangeModeConfig> = {
  drivingTime: {
    unit: 'min',
    defaultValue: 10,
    max: 60,
    sliderMajorTickCount: 6,
    sliderPlaceholder: 'Fahrzeit',
    sliderSuffix: 'min',
    validationLabel: 'Fahrzeit',
    textFn: (value: number, rangeFrom?: number) =>
      rangeFrom != null
        ? `${rangeFrom}-${value} min Fahrzeit`
        : `${value} min Fahrzeit`,
  },
  distance: {
    unit: 'km',
    defaultValue: 25,
    max: 100,
    sliderMajorTickCount: 5,
    sliderPlaceholder: 'Distanz',
    sliderSuffix: 'km',
    validationLabel: 'Distanz',
    textFn: (value: number, rangeFrom?: number) =>
      rangeFrom != null
        ? `${rangeFrom}-${value}km Distanz`
        : `${value}km Distanz`,
  },
};

